import { API_URL } from '../config/api';

export async function saveBpmWithVersionRetry({
  sessionCode,
  bpm,
  token,
  expectedVersion,
  maxRetries = 3,
  keepalive = false,
  fetchImpl = fetch,
  onVersionSync = () => {}
}) {
  const sendPatch = (version) => fetchImpl(`${API_URL}/api/sessions/${sessionCode}`, {
    method: 'PATCH',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({
      bpm,
      ...(Number.isInteger(version) ? { expectedVersion: version } : {})
    })
  });

  let version = expectedVersion;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await sendPatch(version);
    // eslint-disable-next-line no-await-in-loop
    const payload = await response.json().catch(() => null);

    if (response.status === 409 && Number.isInteger(payload?.currentVersion)) {
      onVersionSync(payload.currentVersion);
      version = payload.currentVersion;
      if (attempt < maxRetries) {
        const backoffMs = 15 + Math.floor(Math.random() * 20);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
        // eslint-disable-next-line no-continue
        continue;
      }
      return {
        ok: false,
        conflict: true,
        retryFailed: true,
        failed: true,
        status: response.status,
        payload
      };
    }

    if (!response.ok) {
      return {
        ok: false,
        conflict: false,
        retryFailed: false,
        failed: true,
        status: response.status,
        payload
      };
    }

    if (Number.isInteger(payload?.session?.version)) {
      onVersionSync(payload.session.version);
    }

    return {
      ok: true,
      conflict: false,
      retryFailed: false,
      failed: false,
      status: response.status,
      payload
    };
  }

  return {
    ok: false,
    conflict: true,
    retryFailed: true,
    failed: true,
    status: 409,
    payload: { error: 'Session version conflict' }
  };
}

/**
 * The chat plugin edit (ChatGPT or Claude) an open studio should load, from a
 * GET /api/sessions/:code/version answer: only a version newer than the one the
 * studio holds, produced by the plugin, and not already loaded. The studio's
 * own saves are never plugin edits, so they never trigger a reload.
 */
export function chatEditToLoad(answer, knownVersion, loadedVersion) {
  const edit = answer?.pluginEdit;
  if (!edit || !Number.isInteger(knownVersion) || !Number.isInteger(answer.version)) return null;
  if (edit.version !== answer.version || answer.version <= knownVersion || answer.version === loadedVersion) return null;
  return edit;
}

export async function fetchCanonicalSessionSnapshot({
  sessionCode,
  token,
  fetchImpl = fetch
}) {
  const response = await fetchImpl(`${API_URL}/api/sessions/${sessionCode}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to refresh canonical session (${response.status})`);
  }

  const payload = await response.json().catch(() => null);
  return payload?.session ?? payload ?? null;
}
